import {
    trackStorefrontInteraction,
    trackVisibleProductRecommendation,
} from '../../common/storefront-tracking/storefront-tracking.js';

const endpoint = '/index.php?route=checkout/cart/trackRecommendation';
const listSelector =
    '[data-product-page-recommendations], [data-cart-add-recommendations], [data-cart-page-recommendations]';
const cardSelector = '.z-product-card[data-recommendation-context-token][data-product-id]';
const observedCards = new WeakSet();
let initialized = false;

function track(card, event, leavingPage = false) {
    if (window.siteIntegrationsConfig?.serverTracking?.recommendationEnabled === false) return;
    const contextToken = card.dataset.recommendationContextToken;
    const productId = card.dataset.productId;
    if (!contextToken || !productId) return;
    const trackUrl = card.closest(listSelector)?.dataset.trackUrl || endpoint;

    const data = {
        event,
        product_id: productId,
        batch_id: card.dataset.recommendationBatchId || '',
        context_token: contextToken,
    };
    try {
        if (window.shopRecommendationTracker?.track(trackUrl, data, leavingPage)) return;
        const deliver = () => {
            try {
                const config = window.siteIntegrationsConfig;
                if (
                    config?.serverTracking?.recommendationEnabled === false ||
                    (config?.privateInitialization &&
                        config.serverTracking?.recommendationEnabled !== true)
                )
                    return;
                const body = new URLSearchParams(data);
                if (typeof navigator.sendBeacon === 'function') {
                    try {
                        if (navigator.sendBeacon(trackUrl, body)) return;
                    } catch {
                        // A failed Beacon must still reach the fetch fallback.
                    }
                }
                void fetch(trackUrl, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                    body,
                    priority: 'low',
                    keepalive: leavingPage,
                }).catch(() => {});
            } catch {
                // Tracking remains optional after delayed initialization too.
            }
        };
        if (window.shopInitialization) {
            void Promise.resolve(window.shopInitialization).then(deliver, deliver);
        } else {
            deliver();
        }
    } catch {
        // Recommendation measurement must never interfere with navigation or cart actions.
    }
}

const visibilityObserver =
    'IntersectionObserver' in window
        ? new IntersectionObserver(
              (entries, observer) => {
                  entries.forEach((entry) => {
                      const card = entry.target;
                      if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
                          window.clearTimeout(card.productRecommendationImpressionTimer);
                          card.productRecommendationImpressionTimer = null;
                          return;
                      }
                      if (card.productRecommendationImpressionTimer) return;
                      card.productRecommendationImpressionTimer = window.setTimeout(() => {
                          card.productRecommendationImpressionTimer = null;
                          if (!card.isConnected || document.visibilityState !== 'visible') return;
                          observer.unobserve(card);
                          trackVisibleProductRecommendation(card);
                          track(card, 'impression');
                      }, 500);
                  });
              },
              { threshold: [0, 0.5] },
          )
        : null;

function observe(root = document) {
    root.querySelectorAll(cardSelector).forEach((card) => {
        if (!card.closest(listSelector)) return;
        if (observedCards.has(card)) return;
        observedCards.add(card);
        if (visibilityObserver) visibilityObserver.observe(card);
    });
}

export function initProductRecommendations() {
    observe();
    if (initialized) return;
    initialized = true;
    document.addEventListener(
        'click',
        (event) => {
            const action = event.target.closest('a[href], [data-catalog-add]');
            const card = action?.closest(cardSelector);
            if (!card?.closest(listSelector)) return;
            const placement = card.closest('[data-cart-add-recommendations]')
                ? 'cart_add'
                : card.closest('[data-cart-page-recommendations]')
                  ? 'cart_page'
                  : 'product_page';
            track(card, 'click', action.matches('a[href]'));
            trackStorefrontInteraction('recommendation_interaction', {
                placement,
                action: action.matches('[data-catalog-add]') ? 'add_to_cart' : 'select_product',
                source_context: `${placement}_recommendation`,
                component_type: 'product_recommendation',
                component_id: `recommendation.${placement}`,
                position: Number(card.dataset.recommendationDisplayPosition) || 0,
                candidate_rank: Number(card.dataset.recommendationCandidateRank) || 0,
                is_exploration: card.dataset.recommendationIsExploration === '1',
                recommendation_batch_id: card.dataset.recommendationBatchId || '',
                recommendation_source: card.dataset.recommendationSource || '',
                product_ids: [Number(card.dataset.productId)],
                target_type: 'product',
                target_id: card.dataset.productId,
            });
        },
        true,
    );
    document.addEventListener('zirbelino:content-updated', (event) => {
        if (event.detail?.root instanceof Element) observe(event.detail.root);
    });
    window.addEventListener('shop:restored', () => observe());
}
